import { cn } from '@workspace/utils';
import { useEffect, useMemo, useState } from 'react';
import type { Path } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { FieldCombobox, FieldInput, type ComboboxOption } from '.';
import { useCountries } from '@workspace/api';

export interface FieldCountrySelectProps<TFormValues extends Record<string, any> = any> {
    name: Path<TFormValues> | string;
    label?: string;
    labelSearch?: string;
    defaultCountry?: string;
    onCountryChange?: (country: string) => void;
    nameBirthCity?: Path<TFormValues> | string;
    nameBirthDepartment?: Path<TFormValues> | string;
    disabled?: boolean;
    required?: boolean;
}

interface CountryResult {
    name: string;
    id: string; // Code ISO à 3 lettres (ex: "FRA")
}

const COUNTRY_CONFIG = {
    minQueryLength: 1,
    placeholder: 'Tapez pour rechercher un pays',
    emptyMessage: 'Aucun pays trouvé.',
    loadingMessage: 'Chargement des pays...',
} as const;

/**
 * Sélecteur de pays avec recherche et champ décomposé
 * - Champ de recherche avec API
 * - Champ pays désactivé par défaut
 * - Activation manuelle si API échoue
 * - Tri automatique avec pays par défaut en premier
 */
export function FieldCountrySelect({
    name,
    label = 'Pays de naissance',
    labelSearch = 'pays de naissance',
    defaultCountry,
    disabled = false,
    onCountryChange,
    nameBirthCity,
    nameBirthDepartment,
}: FieldCountrySelectProps) {
    const methods = useFormContext();
    const [searchQuery, setSearchQuery] = useState('');
    const [manualMode, setManualMode] = useState(false);
    const queryCountries = useCountries();

    // Valeur courante du pays (code ISO)
    const currentCountryCode = methods.watch(name);

    // Trouver le nom complet du pays pour l'affichage
    const displayCountryName = useMemo(() => {
        if (!currentCountryCode || !queryCountries.data?.data) return '';
        const country = queryCountries.data.data.find((c: any) => c.id === currentCountryCode);
        return country?.name?.toUpperCase() || currentCountryCode;
    }, [currentCountryCode, queryCountries.data]);

    // Bascule en mode manuel si l'API échoue
    useEffect(() => {
        if (queryCountries.isError) setManualMode(true);
    }, [queryCountries.isError]);

    // Options de recherche
    const options: ComboboxOption<CountryResult>[] = useMemo(() => {
        if (!Array.isArray(queryCountries.data?.data) || queryCountries.isError) return [];

        const filtered = queryCountries.data.data.filter(
            (country: any) => searchQuery.length < COUNTRY_CONFIG.minQueryLength || country.name.toLowerCase().includes(searchQuery.toLowerCase()),
        );

        const sorted = [...filtered].sort((a: any, b: any) => {
            if (a.name === defaultCountry) return -1;
            if (b.name === defaultCountry) return 1;
            return a.name.localeCompare(b.name);
        });

        return sorted.map((country: any) => ({
            value: `${country.name}-${country.id}`, // Valeur unique
            label: country.name.toUpperCase(),
            data: {
                name: country.name.toUpperCase(),
                id: country.id,
            },
        }));
    }, [queryCountries.data, queryCountries.isError, searchQuery, defaultCountry]);

    // Handler de sélection : remplit le champ pays
    const handleCountrySelection = (option: ComboboxOption<CountryResult> | null) => {
        if (!option?.data) return;
        const { id: countryId } = option.data;

        // Stocker le code ISO du pays (ex: "FRA" pour France) et non le nom
        methods.setValue(name, countryId, { shouldValidate: true });

        // Réinitialiser les champs de naissance quand le pays change
        if (nameBirthCity) {
            methods.setValue(nameBirthCity, '');
        }
        if (nameBirthDepartment) {
            methods.setValue(nameBirthDepartment, '');
        }

        if (onCountryChange) {
            onCountryChange(countryId || '');
        }
    };

    // Init search avec valeur existante
    useEffect(() => {
        if (displayCountryName && !searchQuery) {
            setSearchQuery(displayCountryName);
        }
    }, [displayCountryName, searchQuery]);

    // Définir le pays par défaut
    useEffect(() => {
        if (options.length > 0 && !methods.getValues(name)) {
            const defaultOption = options.find((opt) => opt.value === defaultCountry);
            if (defaultOption) {
                methods.setValue(name, defaultOption.value);
                if (onCountryChange) {
                    onCountryChange(defaultOption.value);
                }
            }
        }
    }, [options, methods, name, defaultCountry, onCountryChange]);

    // Effet pour synchroniser le champ d'affichage avec le code ISO
    useEffect(() => {
        if (currentCountryCode && displayCountryName) {
            methods.setValue(`${name}-display` as any, displayCountryName, { shouldValidate: false });
        }
    }, [currentCountryCode, displayCountryName, name, methods]);

    return (
        <div className={cn('grid gap-4 w-full', !manualMode && 'md:grid-cols-2')}>
            {!manualMode && (
                <FieldCombobox
                    label={`Rechercher un ${labelSearch}`}
                    name={`${name}-search`}
                    options={options}
                    required={false}
                    disabled={disabled}
                    placeholder={COUNTRY_CONFIG.placeholder}
                    loading={queryCountries.isFetching}
                    loadingMessage={COUNTRY_CONFIG.loadingMessage}
                    emptyMessage={COUNTRY_CONFIG.emptyMessage}
                    minQueryLength={COUNTRY_CONFIG.minQueryLength}
                    filterOptions={false}
                    onInputChange={setSearchQuery}
                    onChange={handleCountrySelection}
                />
            )}

            {/* Champ caché pour stocker le code ISO */}
            <input type="hidden" {...methods.register(name)} />

            {/* Champ visible pour afficher le nom complet */}
            <FieldInput
                label={label}
                name={`${name}-display`}
                disabled={!manualMode}
                required={false}
                placeholder="Ex: France"
            />
        </div>
    );
}
